import { forwardRef, type InputHTMLAttributes } from "react";

import { cn } from "@/src/lib/utils";
import { Text } from "@/src/components/ui/Typography";
import { textVariants } from "@/src/components/ui/Typography/typographyVariants";

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
}

const FLOAT_CLASSES =
  "peer-focus:top-0 peer-focus:scale-75 peer-focus:text-neutral-500 " +
  "peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:scale-75 peer-[:not(:placeholder-shown)]:text-neutral-500";

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, required, error, hint, id, name, className, ...props },
  ref,
) {
  const inputId = id ?? name;

  return (
    <div className="flex flex-col">
      <div className="relative">
        <input
          ref={ref}
          id={inputId}
          name={name}
          required={required}
          placeholder=" "
          className={cn(
            textVariants({ size: "bodyMD" }),
            "peer w-full border-b bg-transparent pt-6 pb-2 text-neutral-700 outline-none transition-colors focus:border-neutral-700 font-medium",
            error ? "border-error" : "border-neutral-300",
            className,
          )}
          {...props}
        />

        {/* Hover line: sweeps left-to-right over the static border below,
            same target color as the old instant hover swap — matches the
            Link button's underline animation. peer-focus:scale-x-0 keeps it
            from fighting the input's own darker focus border when both
            states are active at once. */}
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 transition-transform duration-400 ease-out peer-hover:scale-x-100 peer-focus:scale-x-0",
            error ? "bg-error" : "bg-neutral-500",
          )}
        />

        <label
          htmlFor={inputId}
          className={cn(
            "pointer-events-none absolute top-6 left-0 origin-left scale-100 text-neutral-400 transition-all duration-200",
            FLOAT_CLASSES,
          )}
        >
          <Text as="span" size="bodyMD" className="text-inherit font-light">
            {label}
            {required && <span className="text-brand ml-1">*</span>}
          </Text>
        </label>
      </div>

      {(error || hint) && (
        <Text
          size="labelSM"
          className={cn("mt-1.5", error ? "text-error" : "text-neutral-500")}
        >
          {error || hint}
        </Text>
      )}
    </div>
  );
});
